import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { MapPin, Plus, AlertTriangle, ThumbsUp, Search, Locate, X, TrendingUp, TrendingDown, Minus, Pencil, Trash2, ImagePlus, Camera, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import MediaGallery from "@/components/MediaGallery";

const TYPE_KEYS = ["marina", "bay", "restaurant", "bar", "fishing_spot", "hazard", "excursion_stop", "anchorage", "berth"];

const TYPE_COLOR = {
  marina: "badge-protected", bay: "badge-protected",
  restaurant: "badge-pending", bar: "badge-pending",
  fishing_spot: "badge-fishing",
  hazard: "badge-rejected",
  excursion_stop: "badge-approved", anchorage: "badge-approved", berth: "badge-approved",
};

export default function Places() {
  const t = useT();
  const [places, setPlaces] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  // search & geo
  const [query, setQuery] = useState("");
  const [geo, setGeo] = useState(null); // { lat, lng }
  const [radius, setRadius] = useState(25);
  const [locating, setLocating] = useState(false);

  const reload = async () => {
    setLoading(true);
    const params = {};
    if (filter !== "all") params.location_type = filter;
    if (query.trim()) params.q = query.trim();
    if (geo) { params.near_lat = geo.lat; params.near_lng = geo.lng; params.radius_km = radius; }
    const { data } = await api.get("/places", { params });
    setPlaces(data); setLoading(false);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter, geo, radius]);

  const onSearchSubmit = (e) => { e.preventDefault(); reload(); };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); toast.success("Location captured"); },
      (err) => { toast.error(err.message || "Location denied"); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const clearGeo = () => { setGeo(null); };

  return (
    <div className="space-y-6" data-testid="places-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("places.subtitle")}</div>
          <h2 className="font-display text-2xl font-bold tracking-tight">{t("places.title")}</h2>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="new-place-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">
          <Plus className="w-4 h-4 mr-1" /> {t("places.add")}
        </Button>
      </div>

      {/* Search & Near-me */}
      <div className="ledger-card p-4 flex flex-wrap gap-3 items-end">
        <form onSubmit={onSearchSubmit} className="flex-1 min-w-[220px]">
          <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("places.searchLabel")}</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("places.searchPlaceholder")}
              data-testid="places-search-input"
              className="bg-[#050A14] border-[#1E293B] pl-9"
            />
            {query && (
              <button type="button" onClick={() => { setQuery(""); setTimeout(reload, 0); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#F8FAFC]"
                data-testid="places-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </form>
        <div>
          <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("places.radius")}</Label>
          <Input type="number" min="1" max="500" value={radius}
            onChange={(e) => setRadius(parseFloat(e.target.value) || 25)}
            data-testid="places-radius-input"
            className="bg-[#050A14] border-[#1E293B] mt-1 w-24"
          />
        </div>
        {geo ? (
          <div className="flex items-center gap-2 text-[11px] font-mono text-[#4DA6FF] bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-md px-3 py-2" data-testid="places-near-active">
            <Locate className="w-3 h-3" />
            {geo.lat.toFixed(4)}, {geo.lng.toFixed(4)} · {radius}km
            <button onClick={clearGeo} className="ml-1 hover:text-[#F87171]" data-testid="places-near-clear"><X className="w-3 h-3" /></button>
          </div>
        ) : (
          <Button
            type="button" onClick={useMyLocation} disabled={locating}
            data-testid="places-near-me-btn"
            className="bg-[#0B1221] border border-[#1E293B] hover:bg-[#131C31] text-[#F8FAFC]"
          >
            <Locate className="w-4 h-4 mr-1" /> {locating ? t("places.locating") : t("places.nearMe")}
          </Button>
        )}
        <Button type="button" onClick={reload} data-testid="places-search-submit" className="bg-[#007AFF] hover:bg-[#0056B3]">
          {t("common.apply")}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>{t("common.all")}</FilterChip>
        {TYPE_KEYS.map((k) => (
          <FilterChip key={k} active={filter === k} onClick={() => setFilter(k)}>{t(`places.types.${k}`)}</FilterChip>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({length: 3}).map((_, i) => <div key={i} className="h-40 ledger-card animate-pulse" />)}
        </div>
      ) : places.length === 0 ? (
        <div className="ledger-card p-12 text-center text-[#94A3B8]">
          <MapPin className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <div className="text-sm">
            {query || geo ? t("places.emptySearch") : t("places.emptyNoSearch")}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {places.map(p => (
            <button
              key={p.id} onClick={() => setSelected(p)}
              data-testid={`place-card-${p.id}`}
              className="ledger-card p-5 text-left hover:-translate-y-0.5 transition-transform"
            >
              <div className="flex items-start justify-between mb-2">
                <span className={`badge-pill ${TYPE_COLOR[p.location_type]}`}>{t(`places.types.${p.location_type}`)}</span>
                <div className="flex items-center gap-2">
                  {p.distance_km != null && (
                    <span className="badge-pill badge-protected" data-testid={`place-distance-${p.id}`}>
                      <Locate className="w-3 h-3" /> {p.distance_km} km
                    </span>
                  )}
                  {p.location_type === "hazard" ? (
                    <AlertTriangle className="w-4 h-4 text-[#F87171]" />
                  ) : p.recommended ? (
                    <ThumbsUp className="w-4 h-4 text-[#34D399]" />
                  ) : null}
                </div>
              </div>
              <div className="font-display text-lg font-bold tracking-tight mb-1">{p.name}</div>
              {(p.latitude != null && p.longitude != null) && (
                <div className="font-mono text-[10px] text-[#94A3B8] mb-2">
                  {p.latitude.toFixed?.(4) || p.latitude}, {p.longitude.toFixed?.(4) || p.longitude}
                </div>
              )}
              {p.general_notes && <div className="text-xs text-[#94A3B8] line-clamp-3">{p.general_notes}</div>}
            </button>
          ))}
        </div>
      )}

      {showNew && <NewPlaceDialog onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); reload(); }} />}
      {selected && <PlaceDetailDialog place={selected} onClose={() => setSelected(null)} onChanged={reload} />}
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-md text-xs border transition-colors ${active ? "bg-[#007AFF]/15 border-[#007AFF]/50 text-[#F8FAFC]" : "bg-[#0B1221] border-[#1E293B] text-[#94A3B8] hover:border-[#334155]"}`}>
      {children}
    </button>
  );
}

function NewPlaceDialog({ onClose, onSaved, editing }) {
  const t = useT();
  const isEdit = !!editing;
  const [form, setForm] = useState(isEdit ? {
    name: editing.name || "",
    location_type: editing.location_type || "marina",
    latitude: editing.latitude != null ? String(editing.latitude) : "",
    longitude: editing.longitude != null ? String(editing.longitude) : "",
    general_notes: editing.general_notes || "",
    dos_note: editing.dos_note || "",
    donts_note: editing.donts_note || "",
    recommended: !!editing.recommended,
  } : { name: "", location_type: "marina", latitude: "", longitude: "", general_notes: "", dos_note: "", donts_note: "", recommended: false });
  const [saving, setSaving] = useState(false);
  // Pending photos queued before the place exists (for new) or to upload to existing place (edit)
  const [pendingPhotos, setPendingPhotos] = useState([]); // [{file, preview}]
  const [uploadingNow, setUploadingNow] = useState(false);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const next = files.filter(f => f.type.startsWith("image/")).map(f => ({
      file: f,
      preview: URL.createObjectURL(f),
    }));
    setPendingPhotos(p => [...p, ...next]);
  };
  const removePendingPhoto = (idx) => setPendingPhotos(p => p.filter((_, i) => i !== idx));

  const fillFromGeo = () => {
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm(f => ({ ...f, latitude: pos.coords.latitude.toFixed(6), longitude: pos.coords.longitude.toFixed(6) })),
      (err) => toast.error(err.message || "Location denied"),
    );
  };

  const submit = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const body = { ...form };
      body.latitude = body.latitude === "" ? null : parseFloat(body.latitude);
      body.longitude = body.longitude === "" ? null : parseFloat(body.longitude);
      let placeId = editing?.id;
      if (isEdit) {
        await api.patch(`/places/${editing.id}`, body);
        toast.success(t("places.updatedToast"));
      } else {
        const { data } = await api.post("/places", body);
        placeId = data.id;
        toast.success(t("places.addedToast"));
      }
      // Upload any queued photos now that we have a placeId
      if (placeId && pendingPhotos.length) {
        setUploadingNow(true);
        let ok = 0;
        for (const p of pendingPhotos) {
          try {
            const fd = new FormData();
            fd.append("file", p.file);
            fd.append("linked_type", "place");
            fd.append("linked_id", placeId);
            await api.post("/media/upload", fd);
            ok++;
          } catch { /* skip and continue */ }
        }
        if (ok) toast.success(t("gallery.uploaded", { count: ok }));
        if (ok < pendingPhotos.length) toast.error(t("gallery.uploadFailed", { count: pendingPhotos.length - ok }));
        setUploadingNow(false);
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-place-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">{isEdit ? t("places.editPlace") : t("places.addPlace")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <F label={t("common.name").toUpperCase()}><Input value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} data-testid="place-name-input" className="bg-[#050A14] border-[#1E293B]" /></F>
          <F label={t("places.type")}>
            <select value={form.location_type} onChange={(e) => setForm(f => ({...f, location_type: e.target.value}))}
              data-testid="place-type-select"
              className="w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
              {TYPE_KEYS.map((k) => <option key={k} value={k}>{t(`places.types.${k}`)}</option>)}
            </select>
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label={t("places.latitude")}><Input value={form.latitude} onChange={(e) => setForm(f => ({...f, latitude: e.target.value}))} placeholder="45.5469" className="bg-[#050A14] border-[#1E293B]" /></F>
            <F label={t("places.longitude")}><Input value={form.longitude} onChange={(e) => setForm(f => ({...f, longitude: e.target.value}))} placeholder="13.7294" className="bg-[#050A14] border-[#1E293B]" /></F>
          </div>
          <button type="button" onClick={fillFromGeo}
            className="w-full px-3 py-2 rounded-md bg-[#050A14] border border-[#1E293B] hover:border-[#334155] text-xs text-[#4DA6FF] flex items-center justify-center gap-2"
            data-testid="place-use-my-location">
            <Locate className="w-3 h-3" /> {t("places.useMyLocation")}
          </button>
          <F label={t("places.notes")}><Textarea value={form.general_notes} onChange={(e) => setForm(f => ({...f, general_notes: e.target.value}))} className="bg-[#050A14] border-[#1E293B]" /></F>
          <F label={t("places.do")}><Textarea value={form.dos_note} onChange={(e) => setForm(f => ({...f, dos_note: e.target.value}))} className="bg-[#050A14] border-[#1E293B]" /></F>
          <F label={t("places.dont")}><Textarea value={form.donts_note} onChange={(e) => setForm(f => ({...f, donts_note: e.target.value}))} className="bg-[#050A14] border-[#1E293B]" /></F>

          {/* Photo picker */}
          <div>
            <Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{t("gallery.photos")}{pendingPhotos.length ? ` (${pendingPhotos.length})` : ""}</Label>
            <input
              ref={fileRef} type="file" accept="image/*,.heic,.heif" multiple
              style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
              onChange={onPickFiles} data-testid="place-photo-input"
            />
            <input
              ref={cameraRef} type="file" accept="image/*" capture="environment"
              style={{ position: "absolute", left: "-9999px", opacity: 0, width: 1, height: 1 }}
              onChange={onPickFiles} data-testid="place-photo-camera-input"
            />
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              <button type="button" onClick={() => fileRef.current?.click()} disabled={saving || uploadingNow}
                data-testid="place-photo-pick-btn"
                className="flex items-center gap-1 text-xs font-mono tracking-widest text-[#4DA6FF] hover:text-[#F8FAFC] disabled:opacity-50">
                {uploadingNow ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                {uploadingNow ? t("assets.fields.uploading") : t("assets.fields.uploadPhoto")}
              </button>
              <button type="button" onClick={() => cameraRef.current?.click()} disabled={saving || uploadingNow}
                data-testid="place-photo-camera-btn"
                className="flex items-center gap-1 text-xs font-mono tracking-widest text-[#4DA6FF] hover:text-[#F8FAFC] disabled:opacity-50">
                <Camera className="w-3.5 h-3.5" />
                {t("assets.fields.takePhoto")}
              </button>
            </div>
            {pendingPhotos.length > 0 && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {pendingPhotos.map((p, idx) => (
                  <div key={idx} className="relative w-20 h-20 rounded-md overflow-hidden border border-[#1E293B]" data-testid={`place-photo-pending-${idx}`}>
                    <img src={p.preview} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePendingPhoto(idx)}
                      className="absolute top-0.5 right-0.5 p-0.5 rounded bg-[#050A14]/80 text-[#F87171] hover:bg-[#7F1D1D] hover:text-white"
                      data-testid={`place-photo-remove-${idx}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {isEdit && (
              <div className="mt-3">
                <MediaGallery linkedType="place" linkedId={editing.id} allowUpload={false} compact />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-[#1E293B]">{t("common.cancel")}</Button>
          <Button onClick={submit} disabled={saving || uploadingNow} data-testid="place-save-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">{saving ? t("common.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlaceDetailDialog({ place, onClose, onChanged }) {
  const t = useT();
  const { user } = useAuth();
  const isAdmin = ["super_admin", "owner_director", "operations_admin"].includes(user?.role);
  const [reviews, setReviews] = useState([]);
  const [showReview, setShowReview] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reviewTypeFilter, setReviewTypeFilter] = useState("all");

  const fetchReviews = async () => {
    const { data } = await api.get(`/places/${place.id}/reviews`);
    setReviews(data);
  };
  useEffect(() => { fetchReviews(); /* eslint-disable-next-line */ }, [place.id]);

  const handleDelete = async () => {
    if (!window.confirm(t("places.deleteConfirm", { name: place.name }))) return;
    setDeleting(true);
    try {
      await api.delete(`/places/${place.id}`);
      toast.success(t("places.deletedToast"));
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setDeleting(false); }
  };

  // Group reviews by year (and optionally by type)
  const grouped = useMemo(() => {
    const filtered = reviewTypeFilter === "all" ? reviews : reviews.filter(r => r.review_type === reviewTypeFilter);
    const byYear = {};
    filtered.forEach(r => {
      const year = (r.created_at || "").slice(0, 4) || "—";
      byYear[year] = byYear[year] || { year, items: [], sum: 0 };
      byYear[year].items.push(r);
      byYear[year].sum += r.rating;
    });
    const list = Object.values(byYear).map(g => ({ ...g, avg: g.items.length ? g.sum / g.items.length : 0 }));
    list.sort((a, b) => b.year.localeCompare(a.year));
    return list;
  }, [reviews, reviewTypeFilter]);

  const reviewTypes = useMemo(() => {
    const s = new Set(reviews.map(r => r.review_type));
    return Array.from(s);
  }, [reviews]);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="place-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2 flex-wrap">
            {place.name}
            <span className={`badge-pill ${TYPE_COLOR[place.location_type]}`}>{t(`places.types.${place.location_type}`)}</span>
            {place.distance_km != null && (
              <span className="badge-pill badge-protected"><Locate className="w-3 h-3" /> {t("places.distanceAway", { km: place.distance_km })}</span>
            )}
          </DialogTitle>
          {isAdmin && (
            <div className="flex items-center gap-1 absolute top-3 right-12" data-testid="place-detail-actions">
              <button onClick={() => setEditing(true)} data-testid={`place-edit-${place.id}`} title={t("common.edit")}
                className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] text-[#94A3B8] hover:text-[#4DA6FF] hover:border-[#4DA6FF]/40">
                <Pencil className="w-3 h-3" />
              </button>
              <button onClick={handleDelete} disabled={deleting} data-testid={`place-delete-${place.id}`} title={t("common.delete")}
                className="p-1.5 rounded-md bg-[#050A14] border border-[#1E293B] text-[#94A3B8] hover:text-[#F87171] hover:border-[#EF4444]/40 disabled:opacity-50">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          )}
        </DialogHeader>
        <div className="space-y-3">
          {place.general_notes && <Block label={t("common.notes")}>{place.general_notes}</Block>}
          {place.dos_note && <Block label={t("places.do")} tone="emerald">{place.dos_note}</Block>}
          {place.donts_note && <Block label={t("places.dont")} tone="amber">{place.donts_note}</Block>}

          <div className="pt-3 border-t border-[#1E293B]">
            <MediaGallery linkedType="place" linkedId={place.id} allowUpload />
          </div>

          <div className="pt-3 border-t border-[#1E293B]">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{t("places.history", { count: reviews.length })}</div>
              <Button size="sm" onClick={() => setShowReview(true)} data-testid="add-review-btn" className="h-7 bg-[#007AFF] hover:bg-[#0056B3] text-xs">
                <Plus className="w-3 h-3 mr-1" /> {t("places.addReview")}
              </Button>
            </div>

            {reviewTypes.length > 1 && (
              <div className="flex flex-wrap gap-1 mb-3" data-testid="review-type-filters">
                <button onClick={() => setReviewTypeFilter("all")}
                  className={`px-2 py-1 rounded text-[10px] font-mono tracking-widest border ${reviewTypeFilter === "all" ? "bg-[#007AFF]/15 border-[#007AFF]/50" : "bg-[#050A14] border-[#1E293B] text-[#94A3B8]"}`}>{t("common.all").toUpperCase()}</button>
                {reviewTypes.map(rt => (
                  <button key={rt} onClick={() => setReviewTypeFilter(rt)}
                    data-testid={`review-type-filter-${rt}`}
                    className={`px-2 py-1 rounded text-[10px] font-mono tracking-widest border capitalize ${reviewTypeFilter === rt ? "bg-[#007AFF]/15 border-[#007AFF]/50" : "bg-[#050A14] border-[#1E293B] text-[#94A3B8]"}`}>{t(`places.reviewTypes.${rt}`)}</button>
                ))}
              </div>
            )}

            {grouped.length === 0 ? (
              <div className="text-xs text-[#94A3B8]">{t("places.noReviews")}</div>
            ) : (
              <div className="space-y-4">
                {grouped.map((g, idx) => {
                  const prev = grouped[idx + 1]; // next in list = previous year (we sorted desc)
                  const delta = prev ? g.avg - prev.avg : null;
                  return (
                    <div key={g.year} data-testid={`year-group-${g.year}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-display text-base font-bold tracking-tight flex items-center gap-2">
                          {g.year}
                          <span className="font-mono text-[10px] text-[#94A3B8]">({g.items.length} {t("places.reviews")})</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[#FBBF24] font-mono text-sm">★ {g.avg.toFixed(1)}</span>
                          {delta != null && (
                            <span className={`badge-pill ${delta > 0 ? "badge-approved" : delta < 0 ? "badge-rejected" : "badge-neutral"}`} data-testid={`year-delta-${g.year}`}>
                              {delta > 0 ? <TrendingUp className="w-3 h-3" /> : delta < 0 ? <TrendingDown className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
                              {delta > 0 ? "+" : ""}{delta.toFixed(1)} vs {prev.year}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {g.items.map(r => (
                          <div key={r.id} className="bg-[#050A14] border border-[#1E293B] rounded-md p-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="badge-pill badge-neutral">{t(`places.reviewTypes.${r.review_type}`) || r.review_type}</span>
                              <span className="text-[#FBBF24] font-mono text-xs">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</span>
                            </div>
                            <div className="text-xs">{r.review_text || "—"}</div>
                            <div className="font-mono text-[10px] text-[#94A3B8] mt-1">— {r.created_by_name} · {fmtDate(r.created_at)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {showReview && <ReviewDialog placeId={place.id} onClose={() => setShowReview(false)} onSaved={async () => { setShowReview(false); fetchReviews(); }} />}
        {editing && (
          <NewPlaceDialog
            editing={place}
            onClose={() => setEditing(false)}
            onSaved={() => { setEditing(false); onChanged?.(); onClose(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({ placeId, onClose, onSaved }) {
  const t = useT();
  const [form, setForm] = useState({ review_type: "restaurant", rating: 4, review_text: "" });
  const submit = async () => {
    try {
      await api.post(`/places/${placeId}/reviews`, form);
      toast.success("Review added"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#0B1221] border-[#1E293B] text-[#F8FAFC]" data-testid="review-dialog">
        <DialogHeader><DialogTitle className="font-display tracking-tight">{t("places.newReview")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <F label={t("places.reviewType")}>
            <select value={form.review_type} onChange={(e) => setForm(f => ({...f, review_type: e.target.value}))}
              className="w-full bg-[#050A14] border border-[#1E293B] rounded-md px-3 py-2 text-sm">
              {["restaurant", "bar", "berth", "anchorage", "fishing"].map(rt => <option key={rt} value={rt}>{t(`places.reviewTypes.${rt}`)}</option>)}
            </select>
          </F>
          <F label={t("places.rating")}>
            <div className="flex gap-1">
              {[1,2,3,4,5].map(n => (
                <button key={n} type="button" onClick={() => setForm(f => ({...f, rating: n}))}
                  data-testid={`review-star-${n}`}
                  className={`w-9 h-9 rounded-md border text-lg ${n <= form.rating ? "bg-[#F59E0B]/15 border-[#F59E0B]/50 text-[#FBBF24]" : "bg-[#050A14] border-[#1E293B] text-[#94A3B8]"}`}>★</button>
              ))}
            </div>
          </F>
          <F label={t("places.comment")}><Textarea value={form.review_text} onChange={(e) => setForm(f => ({...f, review_text: e.target.value}))} className="bg-[#050A14] border-[#1E293B]" /></F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-transparent border-[#1E293B]">{t("common.cancel")}</Button>
          <Button onClick={submit} data-testid="review-save-btn" className="bg-[#007AFF] hover:bg-[#0056B3]">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div><Label className="text-[10px] font-mono tracking-widest text-[#94A3B8]">{label}</Label><div className="mt-1">{children}</div></div>;
}

function Block({ label, children, tone }) {
  const toneMap = { emerald: "border-l-[#10B981]", amber: "border-l-[#F59E0B]" };
  return (
    <div className={`border-l-2 ${toneMap[tone] || "border-l-[#4DA6FF]"} pl-3`}>
      <div className="font-mono text-[10px] tracking-widest text-[#94A3B8]">{label.toUpperCase()}</div>
      <div className="text-sm">{children}</div>
    </div>
  );
}
